import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Phone } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Success() {
  const t = useT();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", data: null, attempts: 0 });
      return;
    }
    let cancelled = false;

    const poll = async (attempts = 0) => {
      if (cancelled) return;
      const MAX = 8;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data, attempts });
          return;
        }
        if (attempts >= MAX) {
          setState({ status: "timeout", data, attempts });
          return;
        }
        setState({ status: "checking", data, attempts });
        setTimeout(() => poll(attempts + 1), 2000);
      } catch (e) {
        if (attempts >= MAX) {
          setState({ status: "error", data: null, attempts });
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      }
    };

    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="success-page" className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-[#2A2A2A]">
        <Link to="/" data-testid="success-back-home" className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#F5F5F0] transition">
          <ArrowLeft className="w-4 h-4" /> {t.success.back} {BRAND.name}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-xl w-full text-center">
          {state.status === "checking" && (
            <div data-testid="success-checking">
              <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto animate-spin" />
              <h1 className="mt-8 font-serif-display text-4xl md:text-5xl tracking-tight">
                {t.success.checking}
              </h1>
              <p className="mt-4 text-[#A3A3A3]">{t.success.checkingNote}</p>
            </div>
          )}

          {state.status === "paid" && (
            <div data-testid="success-paid">
              <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto" strokeWidth={1.5} />
              <h1 className="mt-8 font-serif-display text-4xl md:text-5xl tracking-tight">
                {t.success.paidTitle}
              </h1>
              <p className="mt-4 text-[#A3A3A3] leading-relaxed">
                {t.success.paidNote}{" "}
                <span className="text-[#F5F5F0]">{state.data?.metadata?.package_name || t.success.paidFallback}</span>.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={BRAND.phoneHref}
                  data-testid="success-call-button"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C49A26] text-[#0A0A0A] px-6 py-3 rounded-sm font-semibold tracking-wide transition"
                >
                  <Phone className="w-4 h-4" /> {t.success.callPrefix} {BRAND.phone}
                </a>
                <Link to="/" className="text-sm text-[#A3A3A3] hover:text-[#F5F5F0] underline underline-offset-4">
                  {t.success.backHome}
                </Link>
              </div>
            </div>
          )}

          {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
            <div data-testid="success-error">
              <XCircle className="w-12 h-12 text-red-400 mx-auto" strokeWidth={1.5} />
              <h1 className="mt-8 font-serif-display text-4xl md:text-5xl tracking-tight">
                {t.success.errorTitle}
              </h1>
              <p className="mt-4 text-[#A3A3A3]">{t.success.errorNote}</p>
              <div className="mt-10">
                <a
                  href={BRAND.phoneHref}
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C49A26] text-[#0A0A0A] px-6 py-3 rounded-sm font-semibold"
                >
                  <Phone className="w-4 h-4" /> {t.success.callPrefix} {BRAND.phone}
                </a>
              </div>
            </div>
          )}

          {state.status === "missing" && (
            <div data-testid="success-missing">
              <h1 className="font-serif-display text-3xl">{t.success.missingTitle}</h1>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#F5F5F0]"
              >
                <ArrowLeft className="w-4 h-4" /> {t.success.returnHome}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
